import React, { useEffect, useMemo, useState } from 'react'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { Col, Container, Form, Row, Spinner } from 'react-bootstrap'
import { Modal } from '../../components/Modal/Modal'
import { getInviteRoleGroups, getRoleGroupForRoles } from './InviteRoleGroups'
import { SelectDropdown, SelectOption } from '../../components/SelectDropdown/SelectDropdown'
import { useGetRolesQuery } from '../../store/role/role'
import { MultiSelectDropdown } from '../../components/MultiSelectDropdown/MultiSelectDropdown'
import { getRoleDisplayName } from '../../components/Roles/utils'
import { Role } from '../../models/Role'
import { useGetGrantByIdQuery, useUpdateGrantByIdMutation } from '../../store/crossAccount/crossAccount'

export interface EditGrantModalProps {
  grantId: string
  show: boolean
  onHide: () => void
}

type FormValues = {
  roleIds: string[]
}

export const EditGrantModal: React.FC<EditGrantModalProps> = ({ grantId, show, onHide }) => {
  const accountId = useAppSelector(selectAccountId)

  const defaultValues = {
    roleIds: [],
  }
  const [updateGrant, { isLoading, error: updateRolesError, isSuccess }] = useUpdateGrantByIdMutation()

  const handleSave = (data: FormValues) => {
    updateGrant({
      accountId,
      grantId,
      roleIds: data.roleIds,
    })
  }

  useEffect(() => {
    if (isSuccess) {
      onHide()
    }
  }, [isSuccess])

  const form = useForm<FormValues>({ defaultValues })
  const { handleSubmit } = form

  const body = (
    <Container>
      <Row className='pb-3'>
        <Col>
          <h4 className='d-flex justify-content-center align-items-center'>Edit Grant</h4>
        </Col>
      </Row>
      <Row>
        <FormProvider {...form}>
          <EditGrantModalForm grantId={grantId} show={show} onHide={onHide} />
        </FormProvider>
      </Row>
    </Container>
  )

  return (
    <Modal
      show={show}
      onHide={onHide}
      header={<></>}
      body={body}
      showFooterActions={true}
      onFooterPrimaryAction={handleSubmit(handleSave)}
      footerPrimaryActionLoading={isLoading}
      centered={true}
    />
  )
}

type RoleDisplay = Role & { disabled: boolean }

const EditGrantModalForm: React.FC<EditGrantModalProps> = ({ grantId }) => {
  const {
    register,
    control,
    setValue,
    formState: { errors },
  } = useFormContext<FormValues>()

  const accountId = useAppSelector(selectAccountId)
  const roleGroups = useMemo(() => getInviteRoleGroups({ onlyCrossAccountGrantable: true }), [])

  const { data: grant, error: userError, isFetching: isFetchingUserData } = useGetGrantByIdQuery({ accountId, grantId }, { refetchOnMountOrArgChange: true })

  const {
    data: roleData,
    error: roleError,
    isFetching: isFetchingRoleData,
  } = useGetRolesQuery(
    {
      accountId: accountId,
      pageRequest: { page: 0, size: 500 },
    },
    { refetchOnMountOrArgChange: true },
  )

  const [areUserRolesInGroup, setAreUserRolesInGroup] = useState<boolean>(false)

  useEffect(() => {
    if (grant) {
      const userRoleGroupName = getRoleGroupForRoles(grant.roleIds)
      if (userRoleGroupName) {
        setAreUserRolesInGroup(true)
      }
      setValue('roleIds', grant.roleIds)
    }
  }, [grant])

  const [roles, setRoles] = useState<RoleDisplay[]>([])

  useEffect(() => {
    if (roleData && grant) {
      const roleIds = roleGroups.map((rg) => rg.roleIds).flat()
      const userRoles = grant.roleIds
      const roles = roleData.data
        .filter((role) => {
          return roleIds.includes(role.id) || userRoles.includes(role.id)
        })
        .map((role) => {
          const roleGroup = getRoleGroupForRoles([role.id])
          return {
            ...role,
            name: roleGroup?.name || role.name,
            disabled: !roleIds.includes(role.id) && userRoles.includes(role.id),
          }
        })
      setRoles(roles)
    }
  }, [roleData, grant])

  const handleRoleGroupSelected = (option: SelectOption) => {
    const roleGroup = roleGroups.find((e) => e.name === option.id)
    setValue('roleIds', roleGroup?.roleIds ?? [])
  }

  return (
    <Container className='d-flex flex-column gap-4 p-0'>
      {isFetchingUserData || isFetchingRoleData ? (
        <div className='d-flex align-items-center justify-content-center'>
          <Spinner />
        </div>
      ) : (
        <>
          <Row>
            <Col>
              <Controller
                name='roleIds'
                control={control}
                render={({ field, fieldState }) => (
                  <>
                    <Form.Label>Roles</Form.Label>
                    {areUserRolesInGroup ? (
                      <SelectDropdown
                        className='zocks-px-0.75 py-2'
                        options={roleGroups.map((roleGroup) => {
                          return { id: roleGroup.name, label: roleGroup.name }
                        })}
                        initialSelectedOption={roleGroups
                          .map((roleGroup) => {
                            return { id: roleGroup.name, label: roleGroup.name }
                          })
                          .find((option) => option.id === getRoleGroupForRoles(field.value)?.name)}
                        onOptionSelected={handleRoleGroupSelected}
                      />
                    ) : (
                      <MultiSelectDropdown
                        register={register}
                        name='roleIds'
                        options={
                          roles.map((role) => {
                            return { id: role.id, label: getRoleDisplayName(role), disabled: role.disabled }
                          }) || []
                        }
                        initialSelectedOptions={field.value}
                      />
                    )}
                  </>
                )}
              />
            </Col>
          </Row>
        </>
      )}
    </Container>
  )
}
